import { ReactNode, useMemo } from "react";
import { useLocation, useHistory, useRouteMatch } from "react-router-dom";
import {
  ChangeTracking,
  VerticalTabsLayout,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";

export interface TabItem {
  path?: string;
  title: string;
  content: ReactNode;
}

interface IVerticalTabs {
  tabs: TabItem[];
  targetOverride?: string[];
  includeChangeTracking?: boolean;
  targetIdentifierType?: string;
}

function cleanTargets(targets: string[]) {
  const filtered = targets.filter((item) => !!item);
  return filtered.length > 0 ? filtered : undefined;
}

const VerticalTabs = ({
  tabs,
  targetOverride,
  includeChangeTracking = false,
  targetIdentifierType,
}: IVerticalTabs): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>();
  const { pathname, search } = useLocation();

  const currentTabs = [...tabs];

  function getTargetIds() {
    if (targetOverride) {
      return cleanTargets(targetOverride);
    }
    const params = new URLSearchParams(search);
    return params.get("targetId") || match.params.id;
  }

  const targetIds = useMemo(getTargetIds, [
    targetOverride,
    search,
    match.params.id,
  ]);

  if (includeChangeTracking && targetIds) {
    currentTabs.push({
      path: "/changeTracking",
      title: t("change_tracking_tab_title"),
      content: (
        <ChangeTracking
          targetId={targetIds}
          targetIdentifierType={targetIdentifierType}
        />
      ),
    });
  }

  return (
    <VerticalTabsLayout
      tabs={currentTabs}
      pathname={pathname}
      routeMatch={match}
      toRoute={history.push}
      queryParams={search}
    />
  );
};

export default VerticalTabs;
